// crewbit-admin/src/hooks/useWalkInQueue.ts
//
// Schedule Module Phase C C5A — pending walk-ins list reader.
// Mirror of crewbit-mobile/hooks/useWalkInQueue.ts; bodies byte-identical modulo
// import paths (per feedback_admin_pair_must_match_mobile_body.md parity rule).
//
// Spec: docs/references/schedule/SCHEDULE_MODULE_SPEC_v1.md §10.

import { useCallback, useEffect, useRef, useState } from 'react';
import { supabase } from '../lib/supabase';

export interface PendingWalkIn {
  id: string;
  business_id: string;
  client_name: string | null;
  client_phone: string | null;
  service_id: string | null;
  notes: string | null;
  created_at: string;
}

export interface UseWalkInQueueResult {
  walkIns: PendingWalkIn[];
  loading: boolean;
  error: string | null;
  refresh: () => Promise<void>;
}

export function useWalkInQueue(businessId: string | undefined): UseWalkInQueueResult {
  const [walkIns, setWalkIns] = useState<PendingWalkIn[]>([]);
  const [loading, setLoading] = useState<boolean>(true);
  const [error, setError] = useState<string | null>(null);
  const mountedRef = useRef(true);

  const fetch = useCallback(async () => {
    if (!businessId) { setLoading(false); setWalkIns([]); return; }
    setLoading(true);
    setError(null);
    const { data, error: err } = await supabase
      .from('bookings')
      .select('id, business_id, client_name, client_phone, service_id, notes, created_at')
      .eq('business_id', businessId)
      .eq('source', 'walk_in')
      .eq('status', 'pending')
      .order('created_at', { ascending: true });

    if (!mountedRef.current) return;

    if (err) {
      setError(err.message);
      setWalkIns([]);
    } else {
      setWalkIns((data ?? []) as PendingWalkIn[]);
    }
    setLoading(false);
  }, [businessId]);

  const fetchRef = useRef(fetch);
  useEffect(() => {
    fetchRef.current = fetch;
  }, [fetch]);

  useEffect(() => {
    void fetchRef.current();
  }, [businessId]);

  useEffect(() => {
    mountedRef.current = true;
    if (!businessId) return;
    const channel = supabase
      .channel(`walk-in-queue-${businessId}`)
      .on(
        'postgres_changes',
        { event: '*', schema: 'public', table: 'bookings', filter: `business_id=eq.${businessId}` },
        () => { void fetchRef.current(); },
      )
      .subscribe();
    return () => {
      mountedRef.current = false;
      void supabase.removeChannel(channel);
    };
  }, [businessId]);

  return { walkIns, loading, error, refresh: fetch };
}
